import React, { useMemo } from 'react';
import { useTranslation } from 'localization';
import type { ApproachDefinition } from 'types/approach';
import OutcomeCardAtom from '../../atoms/marketplace/OutcomeCardAtom';
import { useSchemaMetadata } from '../../../hooks/useSchemaMetadata';
import {
  deriveOutcomes,
  type OutcomeCard,
} from '../../../utils/marketplace/deriveOutcomes';
import './OutcomeCardsMolecule.scss';

// Outcome cards that reference a schema as data carry the schema's
// `system_name` in their interpolation params under one of these keys.
// We resolve those values to the schema's friendly display name before
// passing to `t()` so the rendered body never shows raw `center_xxx` strings.
const SCHEMA_PARAM_KEYS = new Set(['schema']);

export interface OutcomeCardsMoleculeProps {
  approach: ApproachDefinition;
  className?: string;
  header?: React.ReactNode;
}

const OutcomeCardsMolecule: React.FC<OutcomeCardsMoleculeProps> = ({
  approach,
  className = '',
  header,
}) => {
  const { t } = useTranslation();

  const cards: OutcomeCard[] = useMemo(
    () => deriveOutcomes(approach),
    [approach],
  );

  // Collect every schema system_name referenced across all cards' params so
  // we can resolve them in a single hook call.
  const schemaSystemNames = useMemo(() => {
    const names = new Set<string>();
    for (const card of cards) {
      for (const params of [card.titleParams, card.bodyParams]) {
        if (!params) continue;
        for (const key of Object.keys(params)) {
          if (SCHEMA_PARAM_KEYS.has(key)) {
            const v = params[key];
            if (typeof v === 'string') names.add(v);
          }
        }
      }
    }
    return Array.from(names);
  }, [cards]);

  const { resolved: schemaMetadata } = useSchemaMetadata(schemaSystemNames);

  // Replace any schema-typed params with their friendly display name before
  // passing to t(). Non-schema params pass through unchanged.
  const friendlyParams = (
    params: Record<string, string | number> | undefined,
  ): Record<string, string | number> | undefined => {
    if (!params) return params;
    let mutated: Record<string, string | number> | null = null;
    for (const key of Object.keys(params)) {
      if (SCHEMA_PARAM_KEYS.has(key)) {
        const v = params[key];
        if (typeof v === 'string') {
          const friendly = schemaMetadata.get(v)?.displayName;
          if (friendly && friendly !== v) {
            mutated ??= { ...params };
            mutated[key] = friendly;
          }
        }
      }
    }
    return mutated ?? params;
  };

  if (cards.length === 0) return null;

  const rootClasses = ['marketplace-outcome-cards', className]
    .filter(Boolean)
    .join(' ');

  return (
    <section className={rootClasses}>
      {header ?? (
        <header className="marketplace-outcome-cards__header">
          {t('marketplace.approachDetail.overview.outcomes.sectionTitle')}
        </header>
      )}
      <div className="marketplace-outcome-cards__grid">
        {cards.map((card, idx) => (
          <OutcomeCardAtom
            key={`${card.titleKey}-${idx}`}
            icon={card.icon}
            hue={card.hue}
            title={t(card.titleKey, friendlyParams(card.titleParams))}
            body={t(card.bodyKey, friendlyParams(card.bodyParams))}
          />
        ))}
      </div>
    </section>
  );
};

OutcomeCardsMolecule.displayName = 'OutcomeCardsMolecule';

export default OutcomeCardsMolecule;
